import {getTestProps, type DataTestAttrs} from '@benchling/js-test-utils';
import {Anchor, createStyles, Flex, Text} from '@mantine/core';
import React, {useState} from 'react';

import {CardSectionEmptyState, CardSectionRowList, CardSectionRowProps} from '../CardSection';
import {
  HOMEPAGE_SECTION_TEST_COMPONENT,
  INITIAL_ROWS_TO_DISPLAY,
  MAX_ROWS_TO_DISPLAY,
} from '../HomepageConstants';
import {getMyWorkEmptyState} from '../HomepageEmptyStates';
import {MyWorkCategory} from './MyWorkConstants';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

const useFooterStyles = createStyles((theme) => ({
  footerRow: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    padding: '6px 16px',
  },
  footerLink: {
    fontSize: theme.fontSizes.lg,
  },
}));

export interface MyWorkRowsFooterAction {
  isDisabled?: boolean;
  label: string;
  onClick: () => void;
}

export interface MyWorkRowsListProps {
  rows: Array<CardSectionRowProps>;
  emptyMessageDataTestAttrs: DataTestAttrs;
  category: MyWorkCategory;
  hasCategoryWork: boolean;
  footerAction?: MyWorkRowsFooterAction;
}

export function MyWorkRowsList({
  rows,
  emptyMessageDataTestAttrs,
  category,
  hasCategoryWork,
  footerAction,
}: MyWorkRowsListProps): JSX.Element {
  const [showMore, setShowMore] = useState(false);
  const maxRows = Math.max(MAX_ROWS_TO_DISPLAY, rows.length);
  const visibleRows = rows.slice(0, showMore ? maxRows : INITIAL_ROWS_TO_DISPLAY);
  const emptyState = getMyWorkEmptyState(category, hasCategoryWork);
  const hasShowMoreToggle = rows.length > INITIAL_ROWS_TO_DISPLAY;
  const enabledFooterAction = footerAction?.isDisabled === true ? undefined : footerAction;
  const {classes} = useFooterStyles();

  return (
    <>
      {rows.length === 0 ? (
        <CardSectionEmptyState {...emptyState} dataTestAttrs={emptyMessageDataTestAttrs} />
      ) : (
        <CardSectionRowList
          rows={visibleRows}
          rowDataTestAttrs={{
            component: TEST_COMPONENT,
            element: `${category}Row`,
          }}
        />
      )}
      {(hasShowMoreToggle || enabledFooterAction != null) && (
        <Flex
          className={classes.footerRow}
          align="center"
          gap="xs"
          {...getTestProps({component: TEST_COMPONENT, element: 'footer'})}
        >
          {hasShowMoreToggle && (
            <Anchor
              component="button"
              className={classes.footerLink}
              onClick={() => {
                setShowMore(!showMore);
              }}
              type="button"
              {...getTestProps({component: TEST_COMPONENT, element: 'showMoreButton'})}
            >
              {showMore ? 'Show less' : 'Show more'}
            </Anchor>
          )}
          {enabledFooterAction != null && hasShowMoreToggle && (
            <Text
              color="dimmed"
              span={true}
              {...getTestProps({component: TEST_COMPONENT, element: 'footerSeparator'})}
            >
              •
            </Text>
          )}
          {enabledFooterAction != null && (
            <Anchor
              component="button"
              className={classes.footerLink}
              onClick={enabledFooterAction.onClick}
              type="button"
              {...getTestProps({component: TEST_COMPONENT, element: 'statusSearchButton'})}
            >
              {enabledFooterAction.label}
            </Anchor>
          )}
        </Flex>
      )}
    </>
  );
}
